/**
 * Corrections-desk (R3) — pure, render-ready view helpers.
 *
 * The R1 primitive persists a `correction {correctedAt, reason, note, supersededBy?}`
 * block plus append-only `transitionLog[]` hops (kind:'correction'/'supersede'). This
 * module turns that raw state into:
 *   - `buildCorrectionView` — a normalized view model the R3 <CorrectionNotice> renders;
 *   - `toPublicCorrection` — the STRUCTURED, machine-readable block for public AI/feed
 *     payloads (ISO dates; deliberately WITHOUT internal transitionLog details).
 *
 * Pure + dependency-free (type-only import) → unit-testable and safe in any bundle.
 */
import type { Correction, TransitionHop } from '../../shared/types/breaking-news';

/** Server-resolved forward pointer for the SUPERSEDED flavor (from `supersededBy`). */
export interface SupersededLink {
  href: string;
  title?: string | null;
}

export interface CorrectionView {
  /** 'superseded' when the doc points forward to a replacement; else 'corrected'. */
  kind: 'corrected' | 'superseded';
  /** ISO timestamp of the latest correction / supersession (or null). */
  at: string | null;
  /** Reader-facing note (falls back to the short `reason` when no note was written). */
  note: string | null;
  /** SUPERSEDED flavor only: the `collection/id` ref of the replacement (or null). */
  supersededBy: string | null;
  /**
   * ISO dates of EARLIER correction/supersede hops (from `transitionLog`), latest excluded.
   * R1 reviewer NIT: a supersede AFTER a correct overwrites the single `correction` field,
   * but the log keeps every hop's date — so an earlier correction stays visible on the record.
   */
  priorDates: string[];
}

const CORRECTION_KINDS = new Set(['correction', 'supersede']);

function trimOrNull(v: unknown): string | null {
  const s = typeof v === 'string' ? v.trim() : '';
  return s || null;
}

/** Reader-facing text for a correction: prefer the note, fall back to the reason. */
function pickNote(correction: Correction): string | null {
  return trimOrNull(correction.note) ?? trimOrNull(correction.reason);
}

/**
 * Build a normalized, render-ready view of a doc's correction state.
 * Returns `null` when the doc is uncorrected (renderer emits nothing → byte-identical).
 */
export function buildCorrectionView(
  correction: Correction | null | undefined,
  transitionLog?: TransitionHop[] | null,
): CorrectionView | null {
  if (!correction || typeof correction !== 'object') return null;

  const supersededBy = trimOrNull(correction.supersededBy);
  const at = trimOrNull(correction.correctedAt);

  // Prefer the log for DATED history (hops carry date + kind, not note text). The last
  // correction-kind hop corresponds to the current `correction` field, so it is excluded
  // from `priorDates` (it is already represented by `at`/`note`).
  const hopDates = Array.isArray(transitionLog)
    ? transitionLog
        .filter((h) => h && CORRECTION_KINDS.has(String(h.kind)))
        .map((h) => trimOrNull(h?.at))
        .filter((x): x is string => x !== null)
    : [];
  const priorDates = hopDates.length > 1 ? hopDates.slice(0, -1) : [];

  return {
    kind: supersededBy ? 'superseded' : 'corrected',
    at,
    note: pickNote(correction),
    supersededBy,
    priorDates,
  };
}

/** The machine-readable correction block for public AI/feed payloads. */
export interface PublicCorrection {
  correctedAt: string | null;
  reason: string | null;
  note: string | null;
  supersededBy: string | null;
}

/**
 * Project a raw `correction` block to the STRUCTURED public shape (ISO dates).
 * Returns `null` when uncorrected so serializers can OMIT the key entirely (keeping
 * uncorrected payloads byte-identical). Never leaks internal transitionLog details.
 */
export function toPublicCorrection(
  correction: Correction | null | undefined,
): PublicCorrection | null {
  if (!correction || typeof correction !== 'object') return null;
  return {
    correctedAt: trimOrNull(correction.correctedAt),
    reason: trimOrNull(correction.reason),
    note: typeof correction.note === 'string' ? correction.note : null,
    supersededBy: trimOrNull(correction.supersededBy),
  };
}

/** Parse a `collection/id` doc ref (the `supersededBy` shape) → {collection,id} or null. */
export function parseDocRef(
  ref: string | null | undefined,
): { collection: string; id: string } | null {
  if (typeof ref !== 'string') return null;
  const idx = ref.indexOf('/');
  if (idx <= 0 || idx === ref.length - 1) return null;
  return { collection: ref.slice(0, idx), id: ref.slice(idx + 1) };
}
